import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Edit, Plus, Search, ShieldCheck, ShieldOff } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { Input } from '@components/Input';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { SimpleTable as Table, TableCell, TableHeader, TableRow } from '@components/Table';
import { companyService } from '@services/companyService';
import { Company, formatCnpj, formatCep, formatPhone, onlyDigits } from '@/types/company';

export const CompanyListView: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const canCreate = checkPermission('companies.create').hasPermission;
  const canWrite = checkPermission('companies.write').hasPermission;

  const [items, setItems] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const trimmed = search.trim();
      const digits = onlyDigits(trimmed);
      const result = await companyService.getCompanies({
        page: currentPage,
        pageSize,
        searchName: trimmed || undefined,
        searchDocument: digits.length >= 4 ? digits : undefined,
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadItems();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <PermissionGuard
      permission="companies.read"
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para visualizar empresas.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-h1">Empresa</h1>
            <p className="text-slate-600">
              Cadastre e mantenha os dados fiscais e cadastrais das empresas do grupo.
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => navigate('/settings/fiscal/companies/new')} icon={<Plus size={20} />}>
              Nova Empresa
            </Button>
          )}
        </div>

        <Card>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Buscar por nome ou CNPJ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                startIcon={<Search size={18} />}
              />
              <Button onClick={handleSearch} icon={<Search size={18} />}>
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">ID</th>
                  <th className="text-left">Empresa</th>
                  <th className="text-left">CNPJ</th>
                  <th className="text-left">IE</th>
                  <th className="text-left">Cidade / UF</th>
                  <th className="text-left">Contato</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><div className="h-4 w-10 animate-pulse rounded bg-slate-200" /></TableCell>
                      <TableCell><div className="h-4 w-48 animate-pulse rounded bg-slate-200" /></TableCell>
                      <TableCell><div className="h-4 w-32 animate-pulse rounded bg-slate-200" /></TableCell>
                      <TableCell><div className="h-4 w-24 animate-pulse rounded bg-slate-200" /></TableCell>
                      <TableCell><div className="h-4 w-28 animate-pulse rounded bg-slate-200" /></TableCell>
                      <TableCell><div className="h-4 w-40 animate-pulse rounded bg-slate-200" /></TableCell>
                      <TableCell><div className="ml-auto h-4 w-20 animate-pulse rounded bg-slate-200" /></TableCell>
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-slate-500">
                      Nenhuma empresa encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.company_id}>
                      <TableCell className="font-mono text-sm">{item.company_id}</TableCell>
                      <TableCell>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-lg bg-emerald-100 p-2 text-emerald-700">
                            <Building2 size={16} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{item.name}</p>
                            <p className="text-xs text-slate-500">{item.email || 'Sem e-mail cadastrado'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{formatCnpj(item.document)}</TableCell>
                      <TableCell>{item.state_registration_tax_id || '—'}</TableCell>
                      <TableCell>
                        {item.addr_city || item.addr_state
                          ? `${item.addr_city || '—'}${item.addr_state ? ` / ${item.addr_state}` : ''}`
                          : '—'}
                        {item.addr_zip && (
                          <p className="text-xs text-slate-500">CEP {formatCep(item.addr_zip)}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <p>{formatPhone(item.phone) || '—'}</p>
                        <p className="text-xs text-slate-500">{item.email || 'Sem e-mail'}</p>
                        {item.has_certificate_password ? (
                          <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">
                            <ShieldCheck size={11} /> Certificado A1 configurado
                          </span>
                        ) : (
                          <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                            <ShieldOff size={11} /> Sem senha A1
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canWrite ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/settings/fiscal/companies/${item.company_id}/edit`)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Somente leitura</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => page - 1)}
              >
                Anterior
              </Button>
              <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((page) => page + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};